import {
  DescriptionOutlined,
  FileDownloadOutlined,
  PersonAddAltOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Menu,
  MenuItem,
  Switch,
  TextField,
} from "@mui/material";
import { isNil } from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Field, useForm, useFormState } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";

import { pdf } from "@react-pdf/renderer";
import { v4 as uuidv4 } from "uuid";
import { formatDate } from "../../../../../_src_shared/utils/functions/formatDate";
import { formatPhone } from "../../../../../_src_shared/utils/functions/formatPhone";
import RffForm from "../../../../../components/finalForm/RffForm";
import RffDollarField from "../../../../../components/finalForm/inputs/RffDollarField";
import RffSelectAutocompleteField from "../../../../../components/finalForm/inputs/RffSelectAutocompleteField";
import RffSelectToggleField from "../../../../../components/finalForm/inputs/RffSelectToggleField";
import RffTextField from "../../../../../components/finalForm/inputs/RffTextField";
import RffTextareaField from "../../../../../components/finalForm/inputs/RffTextareaField";
import RffGroup from "../../../../../components/finalForm/shared/RffGroup";
import RffInputWrapper from "../../../../../components/finalForm/shared/RffInputWrapper";
import SelectAutoInput from "../../../../../components/inputs/SelectAutoInput";
import Flx from "../../../../../components/layout/Flx";
import PdfPreviewer from "../../../../../components/pdf/components/PdfPreviewer";
import FundingShieldPDF from "../../../../../components/pdf/FundingShieldPDF/FundingShieldPDF";
import Txt from "../../../../../components/typography/Txt";
import { selectOptionsUsaStates } from "../../../../../constants/selectOptions/selectOptionsUsaStates";
import { useLoanFilesHook } from "../../../../../hooks/useLoanFilesHook";
import { useLoanTermsHook } from "../../../../../hooks/useLoanTermsHook";
import { useModelFieldOptions } from "../../../../../hooks/useModelFieldOptions";
import { useUnderwritingHook } from "../../../../../hooks/useUnderwritingHook";
import { useEnsureCorrespondents } from "../../../../../hooks/useEnsureReferenceData";
import { loanDrilldownSet } from "../../../../../redux/actions/loanDrilldownActions";
import { filePreviewSetData } from "../../../../../redux/reducers/filePreviewReducer";
import { loanFetchedDataLoanTermsSet } from "../../../../../redux/reducers/loanFetchedDataReducer";
import colors, { slate, status } from "../../../../../theme/colors";
import { addressLabel } from "../../../../../_src_shared/utils/functions/addressLabel";

/**
 * Funding Shield — the FS Sheet: values on the left, live PDF on the right.
 *
 * FIELDS. The form is the FS Sheet's own field list, in the sheet's own order
 * and wording (Loan No. → Lender Name and Address → Borrower Type / Name /
 * Trust-Entity Name → Subject Property Address / City / State / Zip → Total Loan
 * Amount → Account Manager → Correspondent → Closing Agent). The previous build
 * had drifted off it: property address was one lumped string instead of the four
 * separate cells Funding Shield expects, "Borrower Name" was missing entirely,
 * and Guarantor Names — not on the sheet — was in its place.
 *
 * PREPOPULATION is derived from the loan wherever the sheet's value already
 * exists in the LOS, rather than left blank for a closer to re-key:
 *   • Correspondent    ← `loan.originator` (the sheet's "Correspondent" IS the
 *                        originating counterparty)
 *   • POC name/email   ← the correspondent's own contact list, picked from a
 *                        dropdown (see CorrespondentPocSelector)
 *   • Borrower Name    ← the loan's guarantor(s), else its first borrower
 *   • Borrower Type    ← derived from the entity: a Trust entityType → "Trust",
 *                        any other entity → "Entity", no entity → "Individual"
 *   • Property cells   ← `subjectProperties[0].address`, split into street /
 *                        city / state / zip
 *   • Closing Agent    ← `loanTerms.titleContact` (name, email, company, address)
 *
 * Two mechanics the split layout needs: preview values are **debounced** (the
 * previewer rebuilds the whole document on every element-identity change, i.e.
 * every keystroke), and the **form spy is off** (`RffForm` defaults it on in
 * development).
 *
 * Funding Shield **status** is a field on the LOAN (`fundingShieldStatus`), not a
 * value on the sheet, so it sits in its own bar and writes straight through
 * `POST /underwriting/update_loan_data/{dealId}`.
 */

const PREVIEW_DEBOUNCE_MS = 450;
const FS_DOC_TYPE = "Funding Shield Request";
const FS_DOC_GROUP = "Title Docs";

/* -------------------------------------------------------------------------- */
/*  Deriving the sheet's values from the loan                                  */
/* -------------------------------------------------------------------------- */

const fullName = (c) =>
  `${c?.firstName || ""} ${c?.lastName || ""}`.trim() || null;

/**
 * The sheet's three-way Borrower Type from the LOS entity. `entityType` is
 * `['LLC','S-Corp','Revocable Trust','Irrecovable Trust','TBD']` on the model,
 * so a trust is detectable by name. (Note the backend's spelling:
 * "Irrecovable".) Everything else defaults to **Entity** — these are
 * entity-borrower deals in practice, and the entity record is often still thin
 * (or the name only lives on loan terms) when the sheet is built, which used to
 * seed "Individual" on a loan that plainly had an LLC. The closer can still
 * flip the chip to Individual.
 */
const deriveBorrowerType = (loan) => {
  const entityType = loan?.borrowerEntity?.entityType || "";
  if (/trust/i.test(entityType)) return "Trust";
  return "Entity";
};

/** Guarantors first — on an entity loan they are who Funding Shield verifies. */
const deriveBorrowerName = (loan) => {
  const borrowers = loan?.borrowers || [];
  const guarantors = borrowers.filter((b) => b?.isGuarantor);
  const pool = guarantors.length ? guarantors : borrowers;
  const names = pool.map(fullName).filter(Boolean);
  return names.length ? names.join(", ") : null;
};

/** The street line only — the sheet keeps city / state / zip as their own cells. */
const streetLine = (address) => {
  if (!address) return null;
  const street = [address.streetNumber, address.streetName]
    .filter((p) => p !== null && p !== undefined && p !== "")
    .join(" ")
    .trim();
  const unit = address.addressUnitNumber || address.unit;
  const line = street || address.displayAddress || addressLabel(address) || null;
  if (!line) return null;
  return unit ? `${line} #${unit}` : line;
};

const deriveProperty = (loan) => {
  const address = (loan?.subjectProperties || [])[0]?.address;
  return {
    subjectPropertyAddress: streetLine(address),
    subjectPropertyCity: address?.city || null,
    subjectPropertyState: address?.state || null,
    subjectPropertyZip: address?.zip ? String(address.zip) : null,
  };
};

const deriveClosingAgent = (loanTerms) => {
  const c = loanTerms?.titleContact;
  return {
    closingAgentPOCName: fullName(c),
    closingAgentPOCEmail: c?.emailAddress || null,
    // Split halves, used only in the picker's Custom entry mode.
    closingAgentPOCFirstName: c?.firstName || null,
    closingAgentPOCLastName: c?.lastName || null,
  };
};

/**
 * A correspondent's contacts, flattened from its three slots
 * (`primaryContact`, `additionalContacts[]`, `correspondentAuthorizedSigners[]`
 * — `app/models/correspondents.py`), de-duplicated by oid, primary first.
 */
const correspondentContacts = (correspondent) => {
  if (!correspondent) return [];
  const slots = [
    [correspondent.primaryContact, true],
    ...(correspondent.additionalContacts || []).map((c) => [c, false]),
    ...(correspondent.correspondentAuthorizedSigners || []).map((c) => [
      c,
      false,
    ]),
  ];
  const seen = new Set();
  const out = [];
  slots.forEach(([contact, isPrimary]) => {
    if (!contact) return;
    const id = contact.oid || contact._id;
    const name = fullName(contact);
    if (!id || !name || seen.has(id)) return;
    seen.add(id);
    out.push({ ...contact, __id: id, __name: name, __primary: isPrimary });
  });
  // Primary to the top even if it also appears in another slot.
  return [...out].sort((a, b) => Number(b.__primary) - Number(a.__primary));
};

/**
 * The loan's correspondent and its contacts, from the CACHED list.
 *
 * `GET /correspondents/list` returns whole documents (contacts included), so
 * this reads `state.fetchedSelections.correspondents` rather than issuing a
 * per-loan fetch. The loan points at its correspondent through
 * `correspondentCompanyId`; matching `originator` against `companyName` is the
 * fallback for loans that predate that field.
 *
 * Shared between the root (which seeds the primary contact into `initialValues`)
 * and the picker (which renders the list), so the two can't disagree about who
 * the primary is.
 */
const useLoanCorrespondent = () => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const correspondents = useSelector(
    (state) => state.fetchedSelections?.correspondents,
  );

  // Cache-gated — a no-op when the list is already loaded.
  useEnsureCorrespondents();

  const correspondent = useMemo(() => {
    const list = Array.isArray(correspondents) ? correspondents : [];
    const id = loanDrilldown?.correspondentCompanyId;
    if (id) {
      const byId = list.find((c) => (c?._id ?? c?.id) === id);
      if (byId) return byId;
    }
    // No name-matching fallback: without correspondentCompanyId the loan is not
    // linked to a correspondent, and matching on the free-text `originator`
    // would resolve contacts for a company the loan never actually points at.
    return null;
  }, [correspondents, loanDrilldown?.correspondentCompanyId]);

  const contacts = useMemo(
    () => correspondentContacts(correspondent),
    [correspondent],
  );

  return { correspondent, contacts };
};

/* -------------------------------------------------------------------------- */
/*  Root                                                                       */
/* -------------------------------------------------------------------------- */

const LoanFeesSubTabFundingShieldRedesign = ({ showHeaderBar = true }) => {
  const loanTerms = useSelector((state) => state?.loanFetchedData?.loanTerms);
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  // The primary contact is seeded here, NOT pushed in from the picker: this
  // object is recomputed whenever the loan or loan-terms identity changes, which
  // reinitializes the form — a value written after the fact got wiped by that,
  // which is why the POC dropdown showed a name while its inputs sat empty.
  const { contacts: correspondentContacts } = useLoanCorrespondent();
  const primaryPoc = useMemo(
    () => correspondentContacts.find((c) => c.__primary) || null,
    [correspondentContacts],
  );

  const initialValues = useMemo(
    () => ({
      // Loan details
      loanNumber:
        loanTerms?.loanId ||
        loanDrilldown?.syncLoanId ||
        loanDrilldown?.loanNumber ||
        null,
      lenderNameAndAddress: loanTerms?.fundingEntity?.mortgageeClause || null,
      totalLoanAmount:
        loanTerms?.totalLoanAmount ?? loanDrilldown?.totalLoanAmount ?? null,

      // Borrower & property
      borrowerType: deriveBorrowerType(loanDrilldown),
      borrowerName: deriveBorrowerName(loanDrilldown),
      borrowerEntityName:
        loanTerms?.entityName ||
        loanDrilldown?.borrowerEntity?.entityName ||
        null,
      ...deriveProperty(loanDrilldown),

      // Account manager — filled by the LOS-user picker from the loan's
      // `accountManager` assignment.
      accountManagerName: null,
      accountManagerEmail: null,
      accountManagerPhone: null,

      // Correspondent — only when the loan is actually LINKED to one.
      //
      // `originator` is a free-text name that can be stale or a typo;
      // `correspondentCompanyId` is the real link to a correspondent record.
      // Without the id there is nothing to resolve contacts against, so a name
      // on its own would print an unverified counterparty on a wire-fraud
      // verification sheet — treated as no data instead.
      correspondentName: loanDrilldown?.correspondentCompanyId
        ? loanDrilldown?.originator || null
        : null,
      correspondentPOCName: primaryPoc?.__name || null,
      correspondentPOCEmail: primaryPoc?.emailAddress || null,

      // Closing agent
      ...deriveClosingAgent(loanTerms),

      // Print control, not a sheet value — see the preview header switch.
      // Off by default: a request sheet reads better without a column of dashes.
      includeEmptyValues: false,
    }),
    [loanTerms, loanDrilldown, primaryPoc],
  );

  const filename = `Funding Shield - ${formatDate(new Date())}.pdf`;

  return (
    <Box
      sx={{
        flexGrow: 1,
        width: "100%",
        height: "100%",
        minHeight: 0,
        display: "flex",
      }}
    >
      {/* formSpy={false}: RffForm turns the live-values JSON panel on in
          development by default; it is noise beside a live preview. */}
      <RffForm
        suppressFooter
        formSpy={false}
        initialValues={initialValues}
        sx={{
          height: "100%",
          minHeight: 0,
          width: "100%",
          // RffForm puts two shared wrappers between us and the grid — a MUI
          // Container and RffGroupContainer — and both are flex items with the
          // default `min-height: auto`, so they refuse to shrink below their
          // content and the left column's overflow never engages. Unlock them
          // here rather than editing the shared components, which every other
          // form in the app depends on.
          "& > .MuiContainer-root": {
            minHeight: 0,
            "& > *": { minHeight: 0 },
          },
        }}
      >
        <FsDocumentProvider>
          <Flx
            column
            sx={{ width: "100%", height: "100%", minHeight: 0, flexGrow: 1 }}
          >
            <FsSheetToolbar filename={filename} />
            <Box
              sx={{
                display: "grid",
                // The grid is a flex ITEM inside RffGroupContainer (a row flex
                // container), so it needs to claim the width and height itself.
                // minHeight:0 is what lets the left column scroll instead of
                // stretching the card — a grid item defaults to min-content height.
                width: "100%",
                flexGrow: 1,
                height: "100%",
                minHeight: 0,
                gridTemplateColumns: "minmax(0, 1fr)",
                // Split only once both halves are usable — a PDF page under
                // ~360px is unreadable and the form needs ~380px before its
                // three-up row (city / state / zip) stops wrapping. The FORM
                // side is the wider of the two: it is where the work happens,
                // and those paired fields need the room more than a preview
                // that only has to stay legible.
                "@media (min-width:1280px)": {
                  gridTemplateColumns: "minmax(380px, 7fr) minmax(360px, 6fr)",
                },
              }}
            >
              {/* The one scrolling region. The card body has no padding of its
                own, so the padding lives here, inside the scroll. */}
              <Box
                sx={{
                  minWidth: 0,
                  minHeight: 0,
                  overflowY: "auto",
                  px: 3.25,
                  pt: 3.25,
                  // Extra bottom padding so the last field clears the scroll end
                  // instead of sitting flush against the card edge.
                  pb: 5,
                  // Tied to the SAME breakpoint as the split above, not MUI's
                  // `lg` (1200px) — at 1200–1280px the layout is still one
                  // column, and the rule was drawing down its right edge with
                  // nothing beside it.
                  "@media (min-width:1280px)": {
                    borderRight: `1px solid ${slate[200]}`,
                  },
                }}
              >
                {showHeaderBar ? <FundingShieldHeaderBar /> : null}
                <FundingShieldFormQuestions />
              </Box>
              <FundingShieldDocumentColumn />
            </Box>
          </Flx>
        </FsDocumentProvider>
      </RffForm>
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/*  Funding Shield status — a LOAN field, not a sheet value                     */
/* -------------------------------------------------------------------------- */

/**
 * `fundingShieldStatus` has no pinned option list on the model yet
 * (`app/models/closing.py:530` — `options: 'tbd'`), so these are the strings the
 * backend itself produces and matches on, copied verbatim:
 *
 *   "Submitted"            — stamped by POST /loan_terms/send_funding_shield_request/{dealId}
 *                            (`app/routers/loan_terms.py:500`)
 *   the Guardian terminals — GUARDIAN_TERMINAL in `app/models/funding_shield_orders.py`,
 *                            the vendor strings that arrive back on the loan
 *
 * Nothing here is invented. The moment the model pins real options,
 * `useModelFieldOptions` picks them up from `/model_schemas/object/underwriting`
 * and this list stops being used.
 */
const FUNDING_SHIELD_STATUS_FALLBACK = [
  "Submitted",
  "Approved",
  "Approved / Archived",
  "Issues Reported",
  "Issue Reported",
  "Rejected / Archived",
];

/**
 * The header bar — everything ABOUT the request, above the two working columns.
 *
 * Three groups, all built from the same `BarField` shell so they read as one row
 * of peers rather than a select next to loose text: the loan's Funding Shield
 * status (the one editable control), the read-only facts (requested date, how
 * many requests are on file), and the actions.
 *
 * It sits inside `RffForm` because Save renders the PDF from the live form
 * values — but `fundingShieldStatus` itself is NOT a form value: it is a field
 * on the LOAN, written straight through
 * `POST /underwriting/update_loan_data/{dealId}`.
 */

/**
 * Every cell in the bar is the same three bands at the same heights — caption,
 * a 40px content row, and a note line — so the captions share one baseline and
 * the values share another regardless of what each cell holds.
 *
 * The note band is ALWAYS rendered, even when empty. That is the whole trick: a
 * helper only under the select (which is what it was) made that one cell taller
 * and knocked every caption beside it out of line.
 */
const BAR_CONTENT_H = 40;

const BarField = ({ label, note, children, sx }) => (
  <Flx column sx={{ minWidth: 0, ...sx }}>
    <Txt
      sx={{
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: slate[400],
        lineHeight: "16px",
        height: 16,
      }}
    >
      {label}
    </Txt>
    <Flx ac sx={{ minHeight: BAR_CONTENT_H }}>
      {children}
    </Flx>
    {/* <Txt
      sx={{
        fontSize: 10.5,
        color: slate[400],
        lineHeight: "16px",
        height: 16,
        mt: 0.4,
      }}
    >
      {note || " "}
    </Txt> */}
  </Flx>
);

export const FundingShieldHeaderBar = () => {
  const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { updateLoanData } = useUnderwritingHook();
  const docs = useSavedFundingShieldDocs();

  const resolvedOptions = useModelFieldOptions(
    "fundingShieldStatus",
    FUNDING_SHIELD_STATUS_FALLBACK,
  );

  // Whatever the loan already carries is ALWAYS selectable, even if it isn't in
  // the list. Without this a stored value outside the list (the Loan Terms tab
  // offers "Not Submitted" / "Received", which this list doesn't) would render
  // the select blank and the next save would silently wipe it.
  const options = useMemo(() => {
    const current = loanDrilldown?.fundingShieldStatus;
    if (!current || resolvedOptions.includes(current)) return resolvedOptions;
    return [current, ...resolvedOptions];
  }, [resolvedOptions, loanDrilldown?.fundingShieldStatus]);

  const savedStatus = loanDrilldown?.fundingShieldStatus ?? "";
  const [value, setValue] = useState(savedStatus);
  const [saving, setSaving] = useState(false);

  // `<input type="date">` only accepts YYYY-MM-DD; the loan carries a full ISO
  // timestamp, so slice the date part rather than parsing (which would shift the
  // calendar day in a negative-offset zone).
  const savedRequested = String(
    loanDrilldown?.fundingShieldRequestedDate || "",
  ).slice(0, 10);
  const [requestedDate, setRequestedDate] = useState(savedRequested);
  const [requestedSaving, setRequestedSaving] = useState(false);

  // Keep in step when the loan is refetched from elsewhere (the email flow
  // stamps both of these server-side).
  useEffect(() => setValue(savedStatus), [savedStatus]);
  useEffect(() => setRequestedDate(savedRequested), [savedRequested]);

  const handleRequestedChange = useCallback(
    (next) => {
      setRequestedDate(next);
      if (next === savedRequested) return;
      setRequestedSaving(true);
      updateLoanData({
        loanId: loanDrilldown?._id,
        // Midday UTC so the stored timestamp can't land on the previous day
        // once it is read back in Eastern.
        data: {
          fundingShieldRequestedDate: next ? `${next}T12:00:00Z` : null,
        },
        onSuccessMsg: "Funding Shield requested date updated",
        onSuccessFn: (response) => dispatch(loanDrilldownSet(response)),
        onFailFn: () => setRequestedDate(savedRequested),
        onCompleteFn: () => setRequestedSaving(false),
      });
    },
    [dispatch, loanDrilldown?._id, savedRequested, updateLoanData],
  );

  const handleChange = useCallback(
    (next) => {
      setValue(next);
      if (next === savedStatus) return;
      setSaving(true);

      // Moving to "Submitted" stamps today's requested date in the SAME write,
      // mirroring what the send-request route does server-side. Only when the
      // date is empty: overwriting an existing one would silently destroy the
      // real audit timestamp on a re-pick. To change a date that already exists,
      // edit the Requested field directly.
      const data = { fundingShieldStatus: next };
      if (next === "Submitted" && !savedRequested) {
        const today = new Date().toISOString().slice(0, 10);
        data.fundingShieldRequestedDate = `${today}T12:00:00Z`;
        setRequestedDate(today);
      }

      updateLoanData({
        loanId: loanDrilldown?._id,
        data,
        onSuccessMsg: "Funding Shield status updated",
        onSuccessFn: (response) => dispatch(loanDrilldownSet(response)),
        // Snap both controls back to what the loan actually carries on failure.
        onFailFn: () => {
          setValue(savedStatus);
          setRequestedDate(savedRequested);
        },
        onCompleteFn: () => setSaving(false),
      });
    },
    [dispatch, loanDrilldown?._id, savedRequested, savedStatus, updateLoanData],
  );

  return (
    <Box
      sx={{
        // Three equal columns on one row. A wrapping flex row let each cell size
        // to its content, so the three never lined up; a grid pins them.
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          sm: "repeat(3, minmax(0, 1fr))",
        },
        gap: 2,
        alignItems: "start",
      }}
    >
      <BarField label="Funding Shield Status">
        <TextField
          select
          size="small"
          fullWidth
          value={value}
          disabled={saving || isNil(loanDrilldown?._id)}
          onChange={(e) => handleChange(e.target.value)}
          SelectProps={{ displayEmpty: true }}
          sx={{ bgcolor: colors.white }}
        >
          <MenuItem value="">
            <em>Not set</em>
          </MenuItem>
          {options.map((o) => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
        </TextField>
      </BarField>

      {/* Editable, at your request. NOTE the normal path is server-side: the
            send-request route stamps this alongside
            fundingShieldStatus='Submitted' and fundingShieldRequestedBy. Setting
            it by hand overwrites that audit timestamp, so it is here as a
            correction tool, not the way a request gets recorded. */}
      <BarField label="Date Requested">
        <TextField
          type="date"
          size="small"
          value={requestedDate}
          disabled={requestedSaving || isNil(loanDrilldown?._id)}
          onChange={(e) => handleRequestedChange(e.target.value)}
          fullWidth
          sx={{ bgcolor: colors.white }}
        />
      </BarField>

      <OnFileField docs={docs} />
    </Box>
  );
};

/**
 * "On file" — every filed request, always reachable.
 *
 * One file: the chip names it and opens it (truncated with an ellipsis rather
 * than widening the column, since a display name is long and the three cells
 * have to stay equal). Several: the chip counts them and opens a menu, and
 * picking one opens the preview. A filed document is never dead text — see the
 * file-preview rule in CLAUDE.md.
 */
const OnFileField = ({ docs }) => {
  const dispatch = useDispatch();
  const [anchor, setAnchor] = useState(null);

  const openDoc = (doc) => {
    setAnchor(null);
    if (doc) dispatch(filePreviewSetData(doc));
  };

  const chipSx = {
    height: 26,
    // Capped so a long display name can't widen the header cell and squeeze
    // the two controls beside it; the label ellipsises instead.
    maxWidth: 190,
    fontSize: 11.5,
    fontWeight: 600,
    // status.success.bg is a very light mint that disappears on the white
    // readout — this uses the solid green as the text and a tinted border so
    // the chip reads as a link.
    bgcolor: status.success.bg,
    color: status.success.solid,
    border: `1px solid ${status.success.fg}55`,
    "&:hover": { bgcolor: "#DCF5E8" },
    ".MuiChip-icon": { color: "inherit", ml: 0.75 },
    ".MuiChip-label": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  };

  return (
    <BarField label="FS Sheet On File">
      {!docs.length ? (
        <Chip
          size="small"
          label="None yet"
          icon={<DescriptionOutlined sx={{ fontSize: "15px !important" }} />}
          sx={{
            // Full width and warm-toned: nothing on file is the state that needs
            // acting on, so it shouldn't read quieter than a filed document.
            width: "100%",
            height: "32px !important",
            fontSize: 11.5,
            fontWeight: 600,
            justifyContent: "flex-start",
            bgcolor: status.warning.bg,
            color: status.warning.fg,
            border: `1px solid ${status.warning.fg}44`,
            ".MuiChip-icon": { color: "inherit", ml: 0.75 },
          }}
        />
      ) : docs.length === 1 ? (
        <Chip
          size="small"
          clickable
          onClick={() => openDoc(docs[0])}
          label={docs[0]?.file_display_name || docs[0]?.fileName || FS_DOC_TYPE}
          icon={<DescriptionOutlined sx={{ fontSize: "15px !important" }} />}
          sx={chipSx}
        />
      ) : (
        <>
          <Chip
            size="small"
            clickable
            onClick={(e) => setAnchor(e.currentTarget)}
            label={`${docs.length} files`}
            icon={<DescriptionOutlined sx={{ fontSize: "15px !important" }} />}
            sx={chipSx}
          />
          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
          >
            {docs.map((doc, i) => (
              <MenuItem
                key={doc?._id || doc?.oid || i}
                onClick={() => openDoc(doc)}
              >
                <Flx column gap={0.1}>
                  <Txt sx={{ fontSize: 12.5, color: slate[800] }}>
                    {doc?.file_display_name || doc?.fileName || FS_DOC_TYPE}
                  </Txt>
                  <Txt sx={{ fontSize: 11, color: slate[400] }}>
                    {i === 0 ? "Latest · " : ""}
                    {formatDate(doc?.uploadDate, "", "MMM d, yyyy")}
                  </Txt>
                </Flx>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
      {/* <BarReadout sx={{ gap: 0.75, minWidth: 0 }}>
      </BarReadout> */}
    </BarField>
  );
};

/* -------------------------------------------------------------------------- */
/*  The sheet's fields                                                         */
/* -------------------------------------------------------------------------- */

/**
 * A hairline between sections. Equal top and bottom spacing so the rule sits
 * centred in the gap rather than hugging the heading below it, and slate-100 so
 * it separates without competing with the field borders.
 */
const SECTION_SX = {
  pt: 4,
  mt: 4,
  borderTop: `1px solid ${slate[100]}`,
};

const FundingShieldFormQuestions = () => (
  <>
    <RffGroup suppressBottomMargin groupLabel="Loan Details" titleType="h3">
      <RffTextField size={6} name="loanNumber" label="Loan No." />
      <RffDollarField
        size={6}
        name="totalLoanAmount"
        label="Total Loan Amount"
      />
      <RffTextareaField
        minHeight={64}
        name="lenderNameAndAddress"
        label="Lender Name and Address"
      />
    </RffGroup>

    <RffGroup
      suppressBottomMargin
      groupLabel="Borrower & Property"
      titleType="h3"
      sx={SECTION_SX}
    >
      {/* The sheet's own three-way. Not a persisted LOS field — it prints on
          the sheet only — so the options are the sheet's, verbatim. */}
      <RffSelectToggleField
        name="borrowerType"
        label="Borrower Type (Individual, Trust, or Entity)"
        options={["Individual", "Trust", "Entity"]}
      />
      <RffTextField size={6} name="borrowerName" label="Borrower Name" />
      <RffTextField
        size={6}
        name="borrowerEntityName"
        label="Borrower Trust/Entity Name"
      />
      <RffTextField
        name="subjectPropertyAddress"
        label="Subject Property Address"
      />
      <RffTextField size={6} name="subjectPropertyCity" label="City" />
      <RffSelectAutocompleteField
        size={3}
        options={selectOptionsUsaStates}
        name="subjectPropertyState"
        label="State"
      />
      <RffTextField size={3} name="subjectPropertyZip" label="Zip" />
    </RffGroup>

    <RffGroup
      suppressBottomMargin
      groupLabel="Account Manager"
      titleType="h3"
      sx={SECTION_SX}
    >
      <AccountManagerSelector />
    </RffGroup>

    <RffGroup
      suppressBottomMargin
      groupLabel="Correspondent"
      titleType="h3"
      sx={SECTION_SX}
    >
      <CorrespondentSelector />
    </RffGroup>

    <RffGroup
      suppressBottomMargin
      groupLabel="Closing Agent"
      titleType="h3"
      sx={SECTION_SX}
    >
      <ClosingAgentSelector />
    </RffGroup>
  </>
);

/* -------------------------------------------------------------------------- */
/*  Contact pickers                                                            */
/* -------------------------------------------------------------------------- */

const CUSTOM_TERM = "__custom__";
const CUSTOM_OPTION = { value: CUSTOM_TERM, label: "Custom entry…" };

/** Shared shell: a labelled picker that fills the fields rendered below it. */
const ContactPicker = ({
  label,
  helperText,
  options,
  value,
  onChange,
  endContent,
  size,
}) => (
  <RffInputWrapper size={size} label={label} helperText={helperText}>
    <Flx ac gap={0.5} sx={{ flexGrow: 1, alignItems: "flex-end" }}>
      <SelectAutoInput
        value={value}
        sx={{ flexGrow: 1 }}
        onChange={onChange}
        fullWidth
        options={options}
      />
      {endContent}
    </Flx>
  </RffInputWrapper>
);

/**
 * Account Manager — picked from LOS USERS, not from the loan's contacts.
 *
 * The account manager is Eastview staff; they are in the user directory
 * (`state.users`, loaded at dashboard bootstrap), not in `loanRelatedContacts`,
 * which is the loan's external parties. The loan already names one on its
 * `accountManager` field (an email — see LOAN_ROLE_OPTIONS in
 * selectOptionsLoanRoles.js), so that user is preselected and their name, email
 * and phone are filled from the directory.
 *
 * Nothing is selected → the three fields stay hidden. They exist to carry what
 * the picker resolved, so an empty picker has nothing for them to show.
 */
const AccountManagerSelector = () => {
  const users = useSelector((state) => state.users);
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { change, getState } = useForm();
  const assignedEmail = loanDrilldown?.accountManager;

  const [selected, setSelected] = useState(assignedEmail || null);
  const prefilledFor = useRef(null);

  const options = useMemo(
    () => [
      CUSTOM_OPTION,
      ...(users || [])
        .map((u) => {
          const email = u?.emailAddress;
          if (!email) return null;
          const name = fullName(u) || email;
          return { value: email, label: `${name} — ${email}` };
        })
        .filter(Boolean),
    ],
    [users],
  );

  const apply = useCallback(
    (email) => {
      const user = (users || []).find((u) => u?.emailAddress === email);
      change("accountManagerName", user ? fullName(user) : null);
      change("accountManagerEmail", email || null);
      change(
        "accountManagerPhone",
        user?.phone_number || user?.phone
          ? formatPhone(user.phone_number || user.phone)
          : null,
      );
    },
    [change, users],
  );

  // Fill from the loan's assigned account manager once the directory is loaded,
  // and only into untouched fields. Same reasoning as the correspondent POC: the
  // users list arrives after first render, so this can't be an initialValue.
  useEffect(() => {
    if (!assignedEmail || prefilledFor.current === assignedEmail) return;
    if (!(users || []).length) return;
    prefilledFor.current = assignedEmail;

    const { values } = getState();
    if (values?.accountManagerName || values?.accountManagerEmail) return;

    setSelected(assignedEmail);
    apply(assignedEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedEmail, users]);

  const onChange = (email) => {
    setSelected(email ?? null);
    if (isNil(email) || email === CUSTOM_TERM) {
      change("accountManagerName", null);
      change("accountManagerEmail", null);
      change("accountManagerPhone", null);
      return;
    }
    apply(email);
  };

  return (
    <>
      <ContactPicker
        label="Select Account Manager"
        options={options}
        value={selected}
        onChange={onChange}
      />
      {isNil(selected) ? null : (
        <>
          <RffTextField
            name="accountManagerName"
            label="Account Manager Name"
          />
          <RffTextField
            size={6}
            name="accountManagerEmail"
            label="Account Manager Email"
          />
          <RffTextField
            size={6}
            name="accountManagerPhone"
            label="Account Manager Phone"
          />
        </>
      )}
    </>
  );
};

/**
 * Correspondent POC — the contacts on the loan's CORRESPONDENT, not on the loan.
 *
 * The selection is DERIVED from the form values rather than held in local state
 * and pushed into them. That direction matters: `initialValues` recomputes
 * whenever the loan or loan-terms object changes identity, which reinitializes
 * the form — a locally-held prefill got silently wiped by that (the dropdown
 * kept showing a name while the two inputs beside it sat empty). The primary
 * contact is seeded in `initialValues` instead, so a reinit reproduces it.
 */
const CorrespondentSelector = () => {
  const correspondents = useSelector(
    (state) => state.fetchedSelections?.correspondents,
  );
  const { change } = useForm();
  const { values } = useFormState();

  // Cache-gated — a no-op when the list is already loaded.
  useEnsureCorrespondents();

  const list = useMemo(
    () => (Array.isArray(correspondents) ? correspondents : []),
    [correspondents],
  );

  const correspondentOptions = useMemo(
    () => [
      CUSTOM_OPTION,
      ...list
        .map((c) => c?.companyName)
        .filter(Boolean)
        .map((name) => ({ value: name, label: name })),
    ],
    [list],
  );

  /**
   * The selection is HELD, not derived from `correspondentName`.
   *
   * Deriving it couldn't represent Custom entry: choosing it clears the name
   * field, and an empty name is indistinguishable from "nothing selected", so
   * the dropdown snapped straight back to blank. `null` / a company name /
   * CUSTOM_TERM are three distinct states and the control needs all three.
   */
  const knownName = list.some(
    (c) => c?.companyName === values?.correspondentName,
  )
    ? values?.correspondentName
    : null;
  const [selected, setSelected] = useState(knownName);
  const seeded = useRef(false);

  // Seed from the loan's originator once the list has loaded.
  useEffect(() => {
    if (seeded.current || !list.length || !knownName) return;
    seeded.current = true;
    setSelected(knownName);
  }, [list.length, knownName]);

  // Contacts follow the PICKED correspondent, not the loan's. Picking a
  // different company has to repoint the POC list, which is why this resolves
  // off `selected` rather than reusing useLoanCorrespondent().
  const picked = useMemo(
    () =>
      selected && selected !== CUSTOM_TERM
        ? list.find((c) => c?.companyName === selected) || null
        : null,
    [list, selected],
  );

  const contacts = useMemo(() => correspondentContacts(picked), [picked]);
  // Whether the POC picker will render anything. When it will not, the
  // correspondent select takes the whole row instead of leaving a dead half.
  const hasPocPicker = contacts.length > 0;
  const isCustom = selected === CUSTOM_TERM;

  const onCorrespondentChange = (name) => {
    setSelected(name ?? null);
    // The POC belongs to whoever was just picked, so it never carries over.
    change("correspondentPOCName", null);
    change("correspondentPOCEmail", null);
    change(
      "correspondentName",
      isNil(name) ? null : name === CUSTOM_TERM ? "" : name,
    );
  };

  return (
    <>
      <RffInputWrapper
        size={hasPocPicker ? 6 : 12}
        label="Select Correspondent"
      >
        <SelectAutoInput
          value={selected}
          onChange={onCorrespondentChange}
          fullWidth
          options={correspondentOptions}
        />
      </RffInputWrapper>

      <CorrespondentPocSelector contacts={contacts} />

      {isNil(selected) ? null : (
        <>
          {/* Only editable under Custom entry. A picked correspondent already
              IS its name, so an input beside it would just be a way to make the
              two disagree. */}
          {isCustom ? (
            <RffTextField name="correspondentName" label="Correspondent Name" />
          ) : null}
          <RffTextField
            size={6}
            name="correspondentPOCName"
            label="Correspondent POC Name"
          />
          <RffTextField
            size={6}
            name="correspondentPOCEmail"
            label="Correspondent POC Email"
          />
        </>
      )}
    </>
  );
};

const CorrespondentPocSelector = ({ contacts }) => {
  const { change } = useForm();
  const { values } = useFormState();

  const options = useMemo(
    () => [
      // Custom entry leads: it is the escape hatch, and a closer looking for it
      // shouldn't scroll past sixteen contacts to find it.
      CUSTOM_OPTION,
      ...contacts.map((c) => ({
        value: c.__id,
        // Primary is flagged ahead of the name so the tag reads first in a list
        // where every other row is just a person.
        label: c.__primary
          ? `(Primary) ${c.__name}`
          : c.emailAddress
            ? `${c.__name} — ${c.emailAddress}`
            : c.__name,
      })),
    ],
    [contacts],
  );

  // Whichever contact matches what is currently in the fields.
  const selected = useMemo(() => {
    const name = values?.correspondentPOCName;
    const email = values?.correspondentPOCEmail;
    if (!name && !email) return null;
    const hit = contacts.find(
      (c) =>
        (email && c.emailAddress === email) || (!email && c.__name === name),
    );
    return hit ? hit.__id : CUSTOM_TERM;
  }, [contacts, values?.correspondentPOCName, values?.correspondentPOCEmail]);

  const onChange = (id) => {
    if (isNil(id) || id === CUSTOM_TERM) {
      change("correspondentPOCName", null);
      change("correspondentPOCEmail", null);
      return;
    }
    const contact = contacts.find((c) => c.__id === id);
    if (!contact) return;
    change("correspondentPOCName", contact.__name);
    change("correspondentPOCEmail", contact.emailAddress || null);
  };

  // With nothing but "Custom entry…" in it the picker is a control that can only
  // clear the two fields below it — so it isn't rendered at all, and the
  // correspondent select above widens to the full row instead.
  if (!contacts.length) return null;

  return (
    <ContactPicker
      size={6}
      label="Correspondent POC"
      options={options}
      value={selected}
      onChange={onChange}
    />
  );
};

/**
 * Closing Agent — picked from the loan's related contacts, defaulting to the
 * title contact already set on Loan Terms.
 *
 * Custom entry splits the name into first/last, because a name typed here can be
 * PROMOTED: the closer can file it back to the loan as a related contact (role
 * "Title Agent") and optionally set it as the title contact on Loan Terms. Those
 * two writes are sequential, not parallel — `set_title_contact` needs the
 * contact the add call just created, so it runs off that response.
 *
 * The sheet itself only ever carries one `closingAgentPOCName`, so the two
 * halves are concatenated back together (see the effect below).
 */
const ClosingAgentSelector = () => {
  const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanTerms = useSelector((state) => state?.loanFetchedData?.loanTerms);
  const { change } = useForm();
  const { values } = useFormState();
  const { addLoanRelatedContact } = useUnderwritingHook();
  const { updateTitleContact } = useLoanTermsHook();

  const [promoting, setPromoting] = useState(false);

  const contacts = useMemo(
    () => loanDrilldown?.loanRelatedContacts || [],
    [loanDrilldown?.loanRelatedContacts],
  );

  /**
   * What the picker opens on.
   *
   *   1. the loan-terms title contact, if one is set — it is the explicit answer;
   *   2. otherwise the sole contact whose `loanRole` is "Title Agent" — exactly
   *      one is unambiguous, so picking it saves a step;
   *   3. otherwise nothing — with several title agents, or none among the
   *      contacts, there is no safe guess, so the closer chooses.
   */
  const defaultSelection = useMemo(() => {
    const titleContactId = loanTerms?.titleContact?._id;
    if (titleContactId) return titleContactId;
    const titleAgents = contacts.filter((c) => c?.loanRole === "Title Agent");
    return titleAgents.length === 1 ? titleAgents[0]?._id || null : null;
  }, [loanTerms?.titleContact?._id, contacts]);

  const [selected, setSelected] = useState(defaultSelection);
  const seededFor = useRef(null);

  // Seed once the contacts have loaded, and only while nothing has been chosen.
  useEffect(() => {
    if (!defaultSelection || seededFor.current === defaultSelection) return;
    seededFor.current = defaultSelection;
    if (selected) return;
    setSelected(defaultSelection);
    const contact = contacts.find((c) => c._id === defaultSelection);
    if (!contact) return;
    change("closingAgentPOCName", fullName(contact));
    change("closingAgentPOCEmail", contact.emailAddress || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSelection, contacts]);

  const options = useMemo(
    () => [
      CUSTOM_OPTION,
      ...contacts
        .map((c) => {
          const name = fullName(c);
          if (!c?._id || !name) return null;
          return {
            value: c._id,
            label: c?.loanRole ? `(${c.loanRole}) ${name}` : name,
          };
        })
        .filter(Boolean),
    ],
    [contacts],
  );

  const isCustom = selected === CUSTOM_TERM;

  // The sheet prints one name. Keep it in step with the two halves while the
  // closer is typing a custom entry.
  useEffect(() => {
    if (!isCustom) return;
    const joined = `${values?.closingAgentPOCFirstName || ""} ${
      values?.closingAgentPOCLastName || ""
    }`.trim();
    if ((values?.closingAgentPOCName || "") !== joined) {
      change("closingAgentPOCName", joined || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCustom,
    values?.closingAgentPOCFirstName,
    values?.closingAgentPOCLastName,
  ]);

  const onChange = (id) => {
    setSelected(id ?? null);
    if (isNil(id)) {
      change("closingAgentPOCName", null);
      change("closingAgentPOCEmail", null);
      return;
    }
    if (id === CUSTOM_TERM) {
      // Seed the split fields from whatever single name is already there.
      const [first, ...rest] = String(values?.closingAgentPOCName || "")
        .trim()
        .split(/\s+/);
      change("closingAgentPOCFirstName", first || null);
      change("closingAgentPOCLastName", rest.join(" ") || null);
      return;
    }
    const contact = contacts.find((c) => c._id === id);
    if (!contact) return;
    change("closingAgentPOCName", fullName(contact));
    change("closingAgentPOCEmail", contact.emailAddress || null);
  };

  /**
   * File the typed contact onto the loan, and optionally promote it to the
   * loan-terms title contact. Two requests in sequence when both are asked for —
   * the second needs the contact the first created.
   */
  const handleAdd = useCallback(
    (alsoSetAsTitleAgent) => {
      const firstName = values?.closingAgentPOCFirstName;
      const lastName = values?.closingAgentPOCLastName;
      const emailAddress = values?.closingAgentPOCEmail;
      if (!firstName && !lastName) return;

      setPromoting(true);
      addLoanRelatedContact({
        dealId: loanDrilldown?._id,
        contactData: {
          firstName: firstName || null,
          lastName: lastName || null,
          emailAddress: emailAddress || null,
          // Defaulted, per the role this picker exists to fill.
          loanRole: "Title Agent",
        },
        onSuccessFn: (loan) => {
          dispatch(loanDrilldownSet(loan));

          // Find the contact that was just created in the refreshed loan.
          const created = (loan?.loanRelatedContacts || [])
            .filter(
              (c) =>
                (emailAddress && c?.emailAddress === emailAddress) ||
                (fullName(c) || "") ===
                  `${firstName || ""} ${lastName || ""}`.trim(),
            )
            .pop();

          if (created?._id) setSelected(created._id);

          if (!alsoSetAsTitleAgent || !created) {
            setPromoting(false);
            return;
          }

          updateTitleContact({
            dealId: loanDrilldown?._id,
            data: created,
            onSuccessFn: (terms) =>
              dispatch(loanFetchedDataLoanTermsSet(terms)),
            onCompleteFn: () => setPromoting(false),
          });
        },
        onFailFn: () => setPromoting(false),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      loanDrilldown?._id,
      values?.closingAgentPOCFirstName,
      values?.closingAgentPOCLastName,
      values?.closingAgentPOCEmail,
    ],
  );

  const canPromote =
    isCustom &&
    Boolean(
      values?.closingAgentPOCFirstName || values?.closingAgentPOCLastName,
    );

  return (
    <>
      <ContactPicker
        label="Closing Agent POC"
        options={options}
        value={selected}
        onChange={onChange}
      />

      {isNil(selected) ? null : isCustom ? (
        <>
          <RffTextField
            size={6}
            name="closingAgentPOCFirstName"
            label="Closing Agent POC First Name"
          />
          <RffTextField
            size={6}
            name="closingAgentPOCLastName"
            label="Closing Agent POC Last Name"
          />
          <RffTextField
            name="closingAgentPOCEmail"
            label="Closing Agent POC Email Address"
          />
          <RffInputWrapper helperText="Optional — files this person onto the loan so it doesn't have to be retyped next time.">
            <Flx wrap gap={1}>
              <Button
                size="small"
                variant="outlined"
                disabled={!canPromote || promoting}
                startIcon={
                  <PersonAddAltOutlined sx={{ fontSize: "17px !important" }} />
                }
                onClick={() => handleAdd(false)}
              >
                Add to loan contacts
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!canPromote || promoting}
                startIcon={
                  <PersonAddAltOutlined sx={{ fontSize: "17px !important" }} />
                }
                onClick={() => handleAdd(true)}
              >
                Add & set as title agent
              </Button>
            </Flx>
          </RffInputWrapper>
        </>
      ) : (
        <>
          <RffTextField
            size={6}
            name="closingAgentPOCName"
            label="Closing Agent POC Name"
          />
          <RffTextField
            size={6}
            name="closingAgentPOCEmail"
            label="Closing Agent POC Email Address"
          />
        </>
      )}
    </>
  );
};

/* -------------------------------------------------------------------------- */
/*  Document column — preview, what's on file, and the two actions             */
/* -------------------------------------------------------------------------- */

/**
 * Live form values, settled. `PdfPreviewer` rebuilds the whole document when its
 * child element identity changes, so feeding it raw `values` re-renders the PDF
 * on every keystroke.
 */
const useDebouncedFormValues = (delay = PREVIEW_DEBOUNCE_MS) => {
  const { values } = useFormState();
  const [settled, setSettled] = useState(values);

  useEffect(() => {
    const id = setTimeout(() => setSettled(values), delay);
    return () => clearTimeout(id);
  }, [values, delay]);

  return settled;
};

/**
 * ONE document, shared by every path that produces a PDF.
 *
 * There are three ways to get this sheet out — Save to loan, "Download this
 * PDF", and the viewer's own download icon — and they must all hand back the
 * SAME bytes. They wouldn't by default: the preview (and therefore the viewer's
 * download) renders from DEBOUNCED values, while a button reading `useFormState`
 * directly renders from live ones, so a click landing inside the debounce window
 * would save a document that differs from the one on screen.
 *
 * So the settled values and the `<FundingShieldPDF>` element are built once here
 * and consumed by all three. Same element identity → same output, and what you
 * save is provably what you were looking at.
 *
 * (The viewer's download button names the file itself — that we can't control.
 * The CONTENT is identical, which is the guarantee that matters.)
 */
const FsDocumentContext = createContext(null);

const FsDocumentProvider = ({ children }) => {
  const settled = useDebouncedFormValues();
  const value = useMemo(
    () => ({
      values: settled,
      element: <FundingShieldPDF formData={settled} />,
    }),
    [settled],
  );
  return (
    <FsDocumentContext.Provider value={value}>
      {children}
    </FsDocumentContext.Provider>
  );
};

const useFsDocument = () => useContext(FsDocumentContext);

/** Funding Shield requests already filed to the loan, newest first. */
const useSavedFundingShieldDocs = () => {
  const docs = useSelector((state) => state.loanDrilldown?.loanDocuments);
  return useMemo(() => {
    const matches = (docs || []).filter(
      (d) => d?.docType === FS_DOC_TYPE && !d?.isHidden,
    );
    return [...matches].sort((a, b) => {
      const at = a?.uploadDate ? new Date(a.uploadDate).getTime() : 0;
      const bt = b?.uploadDate ? new Date(b.uploadDate).getTime() : 0;
      return bt - at;
    });
  }, [docs]);
};

/**
 * The workspace toolbar, spanning BOTH columns.
 *
 * It reads as one bar because it belongs to one thing — the sheet you are
 * building. "FS Sheet Builder" is deliberate: the earlier "FS Sheet preview"
 * label could only honestly sit over the preview column, because it named the
 * right half. A name for the whole workspace lets the toolbar span the whole
 * workspace, and puts Save (the pane's primary action) where a primary action
 * belongs instead of inside one column's sub-header.
 */
const FsSheetToolbar = ({ filename }) => (
  <Flx
    wrap
    jb
    ac
    gap={2}
    sx={{
      // 26px — the same gutter the form column uses, so the bar's title sits on
      // the same left edge as the field labels underneath it.
      px: 3.25,
      py: 1.5,
      // Ruled top and bottom so the tinted band reads as its own row between the
      // pane header and the workspace, rather than as part of either.
      borderTop: `1px solid ${slate[200]}`,
      borderBottom: `1px solid ${slate[200]}`,
      bgcolor: slate[50],
      flex: "none",
    }}
  >
    <Txt sx={{ fontSize: 14, fontWeight: 600, color: slate[800] }}>
      FS Sheet Builder
    </Txt>
    {/* 14px between the toggle and the buttons: enough that the switch reads as
        a setting rather than as a third button in the row. */}
    <Flx wrap ac gap={1.75}>
      <PrintSwitch name="includeEmptyValues" label="Show empty values" />
      <FundingShieldActions filename={filename} />
    </Flx>
  </Flx>
);

/**
 * Both toolbar buttons share one size so the pair reads as a set rather than as
 * two unrelated controls: 38px tall (matching the status-bar fields in the pane
 * header above), 8px radius, 13px/600 sentence-case label.
 */
const TOOLBAR_BTN_SX = {
  height: 38,
  px: 2,
  borderRadius: "8px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  whiteSpace: "nowrap",
  flex: "none",
};

/**
 * A print control — a form value that changes how the DOCUMENT renders rather
 * than what it says. Lives in the toolbar, beside the Save it affects.
 */
const PrintSwitch = ({ name, label }) => (
  <Field name={name} type="checkbox">
    {({ input }) => (
      <FormControlLabel
        sx={{ mr: 0, ml: 0, gap: 1 }}
        control={
          <Switch
            size="small"
            checked={input.checked}
            onChange={(e) => input.onChange(e.target.checked)}
          />
        }
        label={<Txt sx={{ fontSize: 13, color: slate[600] }}>{label}</Txt>}
      />
    )}
  </Field>
);

/**
 * The actions, in the header bar so every option a closer has is visible before
 * they scroll — they used to be full-size buttons buried under a long form.
 *
 *   Save     — the sheet as currently previewed, filed to the loan as a Title Doc
 *   On file  — open a request already filed (a menu when there are several)
 *   Email    — the request out to the closing agent; only once something is filed
 *
 * Downloading is NOT here: it belongs on the preview header, next to the
 * document it downloads, so there is no ambiguity about which version you get.
 */
/**
 * Label wording, deliberately:
 *   "Download PDF"           — NOT "Save to Desktop". It goes wherever the
 *                              browser is set to download, which usually is not
 *                              the desktop; naming a location the app cannot
 *                              control is a promise it breaks.
 *   "Add to Loan Documents"  — names the tab the closer can actually go look in,
 *                              and "Add" is honest that this appends a new
 *                              version rather than replacing the last one.
 *   "Download & add to loan" — both of the above, from one render.
 */
const FundingShieldActions = ({ filename }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { loading, uploadLoanDocs } = useLoanFilesHook();
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  // The exact document on screen — see FsDocumentProvider. Both buttons render
  // from this one element, so they cannot produce different PDFs.
  const { element } = useFsDocument();

  const saveToDesktop = useCallback(
    async (blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    [filename],
  );

  const saveToLoan = useCallback(
    (blob) =>
      uploadLoanDocs({
        loanId: loanDrilldown?._id,
        // Wrapped objects, not raw Files — the hook expects this shape.
        files: [
          {
            id: uuidv4(),
            file: new File([blob], "funding-shield.pdf", {
              type: "application/pdf",
            }),
            name: "funding-shield.pdf",
            file_display_name: filename,
            docGroup: FS_DOC_GROUP,
            docType: FS_DOC_TYPE,
          },
        ],
        onSuccessMsg: "Funding Shield added to Loan Documents",
        onSuccessFn: (data) => dispatch(loanDrilldownSet(data)),
      }),
    [dispatch, filename, loanDrilldown?._id, uploadLoanDocs],
  );

  /**
   * One render, then fan out to the chosen target. Rendering from the shared
   * element (rather than rebuilding per button) is what guarantees the file you
   * download and the file on the loan are the same bytes.
   */
  const handleSave = useCallback(
    async (toDesktop, toLoan) => {
      setBusy(true);
      try {
        const blob = await pdf(element).toBlob();
        if (toDesktop) await saveToDesktop(blob);
        if (toLoan) await saveToLoan(blob);
      } finally {
        setBusy(false);
      }
    },
    [element, saveToDesktop, saveToLoan],
  );

  // Two buttons, not a menu: there are only two destinations and both are one
  // click deep, so a popover just adds a step. Download is the lower-stakes of
  // the two (nothing leaves this machine), so it takes the outlined treatment
  // and filing to the loan keeps the emphasis.
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        disabled={busy}
        startIcon={<FileDownloadOutlined sx={{ fontSize: "17px !important" }} />}
        onClick={() => handleSave(true, false)}
        sx={{
          ...TOOLBAR_BTN_SX,
          // Background AND text color together: the bar behind it is tinted, so
          // the button needs its own white fill to read as a raised control, and
          // the theme's teal action color is the right label color on that fill.
          bgcolor: colors.white,
          color: colors.tintFg,
          borderColor: slate[300],
          "&:hover": {
            bgcolor: colors.teal50,
            borderColor: colors.teal200,
            color: colors.tintFg,
          },
        }}
      >
        Download PDF
      </Button>
      <Button
        size="small"
        variant="contained"
        loading={loading || busy}
        startIcon={<DescriptionOutlined sx={{ fontSize: "17px !important" }} />}
        onClick={() => handleSave(false, true)}
        sx={TOOLBAR_BTN_SX}
      >
        Add to loan documents
      </Button>
    </>
  );
};

const FundingShieldDocumentColumn = () => {
  const { element } = useFsDocument();

  return (
    // No sticky any more: the FORM column scrolls inside a fixed-height card, so
    // this column just fills its half and stays put — which is what sticky was
    // trying to approximate.
    //
    // The column is a MATTE, not a frame flush to the card edge: a tinted
    // surface with a 26px inset, holding the viewer as a rounded, shadowed
    // object. The viewer's own chrome is the browser's dark PDF toolbar, so
    // running it edge-to-edge against a white card put a hard black rectangle in
    // the corner of the pane; inset and rounded, it reads as a document sitting
    // on a light table instead.
    <Box
      sx={{
        minWidth: 0,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: slate[50],
        p: 3.25,
      }}
    >
      <Box
        sx={{
          minWidth: 0,
          minHeight: 0,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          borderRadius: "12px",
          // The iframe fills the box, so the radius only bites with `hidden` —
          // without it the viewer's square corners sit on top of the rounding.
          overflow: "hidden",
          // Shows through while the document renders, and behind the viewer's
          // own letterboxing around the page. Matching the toolbar's dark chrome
          // keeps the frame from flashing white on every rebuild.
          bgcolor: slate[800],
          boxShadow:
            "0 2px 4px rgba(15,23,42,.06), 0 10px 28px rgba(15,23,42,.10)",
        }}
      >
        {/* Fills whatever height the card gives it, rather than guessing at a
            viewport offset — the card is already sized to the viewport. */}
        <PdfPreviewer
          sx={{
            overflow: "hidden",
            flexGrow: 1,
            minHeight: 0,
            // PDFViewer renders a bare <iframe> and passes only width/height
            // through, so it keeps the UA's 2px inset border — a grey hairline
            // inside the dark frame. Reset it here rather than in the shared
            // previewer, which every other PDF surface uses as-is.
            "& iframe": { border: 0, display: "block" },
          }}
          height="100%"
        >
          {element}
        </PdfPreviewer>
      </Box>
    </Box>
  );
};

export default LoanFeesSubTabFundingShieldRedesign;
